// @flow

import React from 'react';
import type { Node } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { Heading, Markdown, Paragraph } from '@riseart/common';
import { convertToReactStyles } from '@riseart/fe-utils';
import { ReadLegalQuery } from 'shared_data/providers/queries/ReadLegal';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';

import { legalAgreementSeparatorCls } from 'shared_components/cms/modules/legal/Agreement.less';

type Props = {
  legal: string,
  attributes?: Object,
};

/**
 * CmsModuleLegalAgreement
 *
 * @param {Props} props
 * @returns {Node}
 */
export const CmsModuleLegalAgreement = ({ legal, attributes }: Props): Node =>
  legal ? (
    <ReadLegalQuery type={legal}>
      {({ loading, data }) => {
        if (!loading && !data) {
          return null;
        }

        return (
          <div
            {...(attributes &&
              Object.keys(attributes).reduce((accumulator, attributeName) => {
                if (!attributes[attributeName]) {
                  return accumulator;
                }

                if (
                  ['id', 'title'].indexOf(attributeName) > -1 ||
                  attributeName.indexOf('data-') > -1
                ) {
                  accumulator[attributeName] = attributes[attributeName];
                } else if (attributeName === 'style') {
                  accumulator[attributeName] = convertToReactStyles(attributes.style);
                } else if (attributeName === 'class') {
                  accumulator.className = attributes[attributeName];
                }

                return accumulator;
              }, {}))}
          >
            <IsomorphicRipple isActive={loading}>
              {data ? (
                <React.Fragment>
                  {data.title ? <Heading tag="h2">{data.title}</Heading> : null}
                  {data.effectiveDate ? (
                    <Paragraph>
                      <FormattedMessage id="components.cms.modules.legal.effectiveDate" />:{' '}
                      <FormattedDate
                        value={new Date(data.effectiveDate)}
                        year="numeric"
                        month="long"
                        day="2-digit"
                      >
                        {(dateLabel: string) => dateLabel}
                      </FormattedDate>
                    </Paragraph>
                  ) : null}
                  <hr className={legalAgreementSeparatorCls} />
                  {data.legalText ? <Markdown text={data.legalText} /> : null}
                  {data.version ? (
                    <Paragraph>
                      <FormattedMessage id="components.cms.modules.legal.version" />: {data.version}
                    </Paragraph>
                  ) : null}
                </React.Fragment>
              ) : null}
            </IsomorphicRipple>
          </div>
        );
      }}
    </ReadLegalQuery>
  ) : null;
